/**
 * Atlas Calendar — Google Calendar sync.
 *
 * Shows a native month-grid view of the user's connected Google Calendar and
 * lets them create / delete events without leaving Strata. Sign-in is a
 * standard OAuth 2.0 flow opened in a popup window; the backend stores refresh
 * tokens per-user and auto-refreshes access tokens.
 */
import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  GoogleLogo, CaretLeft, CaretRight, Plus, ArrowClockwise, LinkBreak,
} from "@phosphor-icons/react";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function sameDay(a, b)   { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function toISO(d)        { return d.toISOString(); }
function toDateStr(d)    { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

function eventStart(ev) {
  if (ev.start?.dateTime) return new Date(ev.start.dateTime);
  if (ev.start?.date) return new Date(ev.start.date + "T00:00:00");
  return null;
}
function eventEnd(ev) {
  if (ev.end?.dateTime) return new Date(ev.end.dateTime);
  if (ev.end?.date) return new Date(ev.end.date + "T00:00:00");
  return null;
}

export default function AtlasCalendar() {
  const [status, setStatus] = useState({ connected: false });
  const [statusLoading, setStatusLoading] = useState(true);
  const [cursor, setCursor] = useState(startOfMonth(new Date())); // first-of-month being viewed
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', event? , date? }

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/atlas/google/status");
      setStatus(data || { connected: false });
    } catch {
      setStatus({ connected: false });
    } finally {
      setStatusLoading(false);
    }
  };

  const loadEvents = async () => {
    if (!status.connected) return;
    setLoading(true);
    try {
      const from = startOfMonth(cursor);
      const to = endOfMonth(cursor);
      const { data } = await api.get("/atlas/calendar/events", {
        params: { time_min: toISO(from), time_max: toISO(to), max_results: 500 },
      });
      setEvents(data?.items || []);
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadEvents(); /* eslint-disable-next-line */ }, [status.connected, cursor]);

  // Listen for the popup postMessage on successful OAuth
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === "atlas-google-connected") {
        toast.success("Google Calendar connected");
        loadStatus();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.get("/atlas/google/login");
      if (!data?.authorization_url) throw new Error("No authorization URL");
      window.open(data.authorization_url, "atlas-google", "width=520,height=680");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? You'll need to reconnect to see events again.")) return;
    try {
      await api.post("/atlas/google/disconnect");
      setStatus({ connected: false });
      setEvents([]);
      toast.success("Disconnected");
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  const createEvent = async (payload) => {
    try {
      await api.post("/atlas/calendar/events", payload);
      toast.success("Event created");
      setDialog(null);
      loadEvents();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };
  const updateEvent = async (id, payload) => {
    try {
      await api.put(`/atlas/calendar/events/${id}`, payload);
      toast.success("Event updated");
      setDialog(null);
      loadEvents();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event from Google Calendar?")) return;
    try {
      await api.delete(`/atlas/calendar/events/${id}`);
      toast.success("Event deleted");
      setDialog(null);
      loadEvents();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    }
  };

  // Build calendar grid (6 rows × 7 cols)
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startDay = first.getDay(); // 0=Sun
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(first);
      d.setDate(1 - startDay + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach((ev) => {
      const s = eventStart(ev);
      if (!s) return;
      const k = toDateStr(s);
      (map[k] ||= []).push(ev);
    });
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (eventStart(a) - eventStart(b))));
    return map;
  }, [events]);

  // ---- Not connected state ----
  if (statusLoading) {
    return <div className="text-sm text-slate-500 py-10 text-center">Checking Google connection…</div>;
  }
  if (!status.connected) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 max-w-2xl mx-auto text-center shadow-sm" data-testid="atlas-calendar-connect">
        <div className="w-14 h-14 rounded-full bg-[#4285F4]/10 flex items-center justify-center mx-auto mb-4">
          <GoogleLogo size={28} weight="bold" className="text-[#4285F4]" />
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-2">Connect your Google Calendar</h2>
        <p className="text-sm text-slate-600 max-w-md mx-auto mb-5">
          Atlas will show your events here, and any events you create inside Strata will sync
          straight into your Google Calendar. Only <strong>you</strong> can see this calendar.
        </p>
        <Button
          onClick={connect}
          size="lg"
          className="bg-[#1f64c2] hover:bg-[#164a97] text-white gap-2"
          data-testid="atlas-google-connect-btn"
        >
          <GoogleLogo size={18} weight="bold" /> Sign in with Google
        </Button>
      </div>
    );
  }

  // ---- Connected: month grid ----
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="atlas-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="atlas-cal-prev">
            <CaretLeft size={14} />
          </Button>
          <h2 className="text-lg font-black tracking-tight" data-testid="atlas-cal-title">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </h2>
          <Button variant="outline" size="sm" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="atlas-cal-next">
            <CaretRight size={14} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(startOfMonth(new Date()))} data-testid="atlas-cal-today">
            Today
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="hidden sm:inline">Connected as</span>
          <span className="font-semibold text-slate-700 truncate max-w-[220px]" data-testid="atlas-google-email">{status.email || ""}</span>
          <Button variant="ghost" size="sm" onClick={loadEvents} title="Refresh" data-testid="atlas-cal-refresh">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
          </Button>
          <Button variant="ghost" size="sm" onClick={disconnect} title="Disconnect" className="text-rose-600 hover:text-rose-700" data-testid="atlas-google-disconnect">
            <LinkBreak size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
        {DAY_NAMES.map((d) => (
          <div key={d} className="px-2 py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden">
        {grid.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, new Date());
          const key = toDateStr(d);
          const dayEvents = eventsByDay[key] || [];
          return (
            <div
              key={i}
              className={`bg-white min-h-[88px] p-1.5 flex flex-col gap-1 ${inMonth ? "" : "bg-slate-50/60"} ${isToday ? "ring-2 ring-inset ring-[#1f64c2]" : ""}`}
              data-testid={`atlas-cal-day-${key}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-[11px] font-semibold ${isToday ? "text-[#1f64c2]" : inMonth ? "text-slate-700" : "text-slate-400"}`}>
                  {d.getDate()}
                </span>
                <button
                  onClick={() => setDialog({ mode: "create", date: key })}
                  className="opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 text-slate-300 hover:text-[#1f64c2]"
                  title="Add event"
                  data-testid={`atlas-cal-add-${key}`}
                >
                  <Plus size={12} />
                </button>
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {dayEvents.slice(0, 4).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => setDialog({ mode: "edit", event: ev })}
                    className="w-full text-left text-[10px] leading-tight rounded px-1 py-0.5 bg-[#1f64c2]/10 text-[#1f64c2] hover:bg-[#1f64c2]/20 truncate"
                    data-testid={`atlas-cal-event-${ev.id}`}
                  >
                    {ev.summary || "(no title)"}
                  </button>
                ))}
                {dayEvents.length > 4 && (
                  <div className="text-[9px] text-slate-500">+{dayEvents.length - 4} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {dialog && (
        <EventDialog
          dialog={dialog}
          onClose={() => setDialog(null)}
          onCreate={createEvent}
          onUpdate={updateEvent}
          onDelete={deleteEvent}
        />
      )}
    </div>
  );
}

function EventDialog({ dialog, onClose, onCreate, onUpdate, onDelete }) {
  const isEdit = dialog.mode === "edit";
  const ev = dialog.event;
  const initialDate = isEdit ? toDateStr(eventStart(ev) || new Date()) : dialog.date;
  const initialTitle = isEdit ? (ev.summary || "") : "";
  const initialAllDay = isEdit ? !!ev.start?.date : true;
  const initialStart = isEdit
    ? (ev.start?.dateTime || `${ev.start?.date}T09:00`)
    : `${initialDate}T09:00`;
  const initialEnd = isEdit
    ? (ev.end?.dateTime || `${ev.end?.date}T10:00`)
    : `${initialDate}T10:00`;

  const [title, setTitle] = useState(initialTitle);
  const [allDay, setAllDay] = useState(initialAllDay);
  const [startVal, setStartVal] = useState(initialStart.slice(0, 16));
  const [endVal, setEndVal] = useState(initialEnd.slice(0, 16));
  const [dateVal, setDateVal] = useState(initialDate);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const submit = () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    const payload = allDay
      ? { title: title.trim(), start: dateVal, end: dateVal, all_day: true, time_zone: tz }
      : { title: title.trim(), start: new Date(startVal).toISOString(), end: new Date(endVal).toISOString(), all_day: false, time_zone: tz };
    if (isEdit) onUpdate(ev.id, payload);
    else onCreate(payload);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="atlas-event-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Syncs directly to your primary Google Calendar.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="atlas-event-title" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} data-testid="atlas-event-allday" />
            All-day
          </label>
          {allDay ? (
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="atlas-event-date" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Starts</Label>
                <Input type="datetime-local" value={startVal} onChange={(e) => setStartVal(e.target.value)} data-testid="atlas-event-start" />
              </div>
              <div>
                <Label className="text-xs">Ends</Label>
                <Input type="datetime-local" value={endVal} onChange={(e) => setEndVal(e.target.value)} data-testid="atlas-event-end" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          {isEdit && (
            <Button variant="ghost" onClick={() => onDelete(ev.id)} className="text-rose-600 hover:text-rose-700 mr-auto" data-testid="atlas-event-delete">
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} data-testid="atlas-event-cancel">Cancel</Button>
          <Button onClick={submit} className="bg-[#1f64c2] hover:bg-[#164a97] text-white" data-testid="atlas-event-save">
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
